import {Vector2} from "@cm/math"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {assertNever} from "@cm/utils"
import {ImageOpNodeTilingFeatureRayCast} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/feature-raycast/node"
import {OperatorCallback} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-callback"
import {Subject, takeUntil} from "rxjs"
import {HelperLineItem} from "@app/textures/texture-editor/operator-stack/operators/tiling/toolbox/helper-lines/helper-line-item"
import {ImageOpNodeTilingDetermineFeatureWavelength} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/determine-feature-wavelength/node"
import {EventEmitter} from "@angular/core"
import {ImageOpHalOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"

export class HelperLineTracer {
    readonly traceComplete = new EventEmitter()

    constructor(
        private callback: OperatorCallback,
        readonly helperLine: HelperLineItem,
    ) {
        helperLine.itemRemove.pipe(takeUntil(this.unsubscribe$)).subscribe(() => this.abort())
        this.performTraceStep$.subscribe((traceInfo) => this.performTraceStep(traceInfo))
        this.scheduleTraceStep("init")
    }

    abort() {
        this.complete()
    }

    private complete() {
        this.isAborted = true
        this.unsubscribe$.next()
        this.unsubscribe$.complete()
        this.performTraceStep$.complete()
        this.imageOpNodeBag.dispose()
        this.traceComplete.emit()
    }

    private scheduleTraceStep(traceState: TraceState) {
        setTimeout(() => this.performTraceStep$.next(traceState), 0)
    }

    private async performTraceStep(traceState: TraceState) {
        const minCorrelationToProceed = 0.1
        const imageOpHalOptions: ImageOpHalOptions = {
            usePhysicalOutputImage: true,
        }

        const sourceImage = this.callback.selectedOperatorInput
        if (!sourceImage) {
            throw new Error("No source image")
        }

        if (this.isAborted || this.helperLine.isRemoved) {
            return
        }

        this.imageOpNodeBag.beginFrame()

        let isComplete = false
        if (traceState === "init") {
            // console.log("Starting feature tracing")
            // console.log(
            //     `Starting at (${helperLine.sourceCastRayOrigin.x}, ${helperLine.sourceCastRayOrigin.y}) to (${helperLine.sourceCastRayTarget.x}, ${helperLine.sourceCastRayTarget.y})`,
            // )
            // this is the first step; let's determine the ideal wavelength
            const determineFeatureWaveLengthNode = this.imageOpNodeBag.getNode("DetermineFeatureWavelength", ImageOpNodeTilingDetermineFeatureWavelength, {
                imageOpHalOptions,
                sourceImage: sourceImage,
                rayOrigin: this.helperLine.sourceCastRayOrigin,
                rayTarget: this.helperLine.sourceCastRayTarget,
            })
            const result = await this.callback.imageOpSystem.evaluate("hal", determineFeatureWaveLengthNode)
            this.helperLine.rescaleSourceTargetDistance(result.waveLength)
            // console.log(`Ideal wave length is ${result.waveLength} with energy ${result.energy}`)

            // result.details.correlationData.forEach((correlation, index) => {
            //     const from = Vector2.fromVector2Like(result.details.searchSegment.from)
            //     const to = Vector2.fromVector2Like(result.details.searchSegment.to)
            //     const referenceLength = Math.round(Vector2.fromVector2Like(result.details.referenceSegment.to).sub(result.details.referenceSegment.from).norm())
            //     const searchLength = Math.round(to.sub(from).norm())
            //     const searchDir = to.sub(from).normalized()
            //     const pos = searchDir.mul(((searchLength - referenceLength) * index) / (result.details.correlationData.length - 1)).add(from)
            //     const dirPerp = to.sub(from).perp().normalized()
            //     new LineItem(this.canvasToolbox, [pos, pos.add(dirPerp.mul(correlation * 10))], 1, new Color(1, 0, 0))
            // })
            // new ArrowItem(this.canvasToolbox, result.details.searchSegment.from, result.nextPosition, 1, new Color(Math.random(), Math.random(), Math.random()))

            // trace in both directions
            this.helperLine.addTailPoint(this.helperLine.sourceCastRayOrigin)
            this.scheduleTraceStep("forward-trace")
        } else {
            const traceLength = Vector2.distance(this.helperLine.sourceCastRayOrigin, this.helperLine.sourceCastRayTarget)
            const linePoints = this.helperLine.points
            let referenceRayOrigin: Vector2
            let referenceRayTarget: Vector2
            let rayOrigin: Vector2
            let rayDirection: Vector2
            switch (traceState) {
                case "forward-trace":
                    referenceRayOrigin = this.helperLine.sourceCastRayOrigin
                    referenceRayTarget = this.helperLine.sourceCastRayTarget
                    if (linePoints.length >= 2) {
                        const lastPoint = linePoints[linePoints.length - 1]
                        const secondLastPoint = linePoints[linePoints.length - 2]
                        rayOrigin = lastPoint
                        rayDirection = lastPoint.sub(secondLastPoint).normalized()
                    } else {
                        rayOrigin = this.helperLine.sourceCastRayOrigin
                        rayDirection = this.helperLine.sourceCastRayTarget.sub(rayOrigin).normalized()
                    }
                    break
                case "backward-trace":
                    referenceRayOrigin = this.helperLine.sourceCastRayTarget
                    referenceRayTarget = this.helperLine.sourceCastRayOrigin
                    if (linePoints.length >= 2) {
                        const firstPoint = linePoints[0]
                        const secondPoint = linePoints[1]
                        rayOrigin = firstPoint
                        rayDirection = firstPoint.sub(secondPoint).normalized()
                    } else {
                        rayOrigin = this.helperLine.sourceCastRayOrigin
                        rayDirection = this.helperLine.sourceCastRayOrigin.sub(this.helperLine.sourceCastRayTarget).normalized()
                    }
                    break
                default:
                    assertNever(traceState)
            }
            const rayTarget = rayOrigin.add(rayDirection.mul(traceLength))

            const featureRayCastNode = this.imageOpNodeBag.getNode("FeatureRayCase", ImageOpNodeTilingFeatureRayCast, {
                imageOpHalOptions,
                sourceImage: sourceImage,
                sourceCastRayOrigin: referenceRayOrigin,
                sourceCastRayTarget: referenceRayTarget,
                currentCastRayOrigin: rayOrigin,
                currentCastRayTarget: rayTarget,
            })
            const result = await this.callback.imageOpSystem.evaluate("hal", featureRayCastNode)

            // console.log(`Best peak along line at (${result.nextPosition.x}, ${result.nextPosition.y}) with value ${result.details.peakValue}`)
            // const showDebugInfo = true
            // if (showDebugInfo) {
            // result.details.correlationData.forEach((correlation, index) => {
            //     const from = Vector2.fromVector2Like(result.details.searchSegment.from)
            //     const to = Vector2.fromVector2Like(result.details.searchSegment.to)
            //     const referenceLength = Math.round(Vector2.fromVector2Like(result.details.referenceSegment.to).sub(result.details.referenceSegment.from).norm())
            //     const searchLength = Math.round(to.sub(from).norm())
            //     const searchDir = to.sub(from).normalized()
            //     const pos = searchDir.mul(((searchLength - referenceLength) * index) / (result.details.correlationData.length - 1)).add(from)
            //     const dirPerp = to.sub(from).perp().normalized()
            //     new LineItem(this.canvasToolbox, [pos, pos.add(dirPerp.mul(correlation * 10))], 1, new Color(1, 0, 0))
            // })
            // new ArrowItem(this.canvasToolbox, helperLine.currentCastRayOrigin, result.nextPosition, 4, new Color(Math.random(), Math.random(), Math.random()))
            // }

            let doneTracing: boolean
            if (result && result.details.peakValue >= minCorrelationToProceed) {
                const newOrigin = result.nextPosition
                switch (traceState) {
                    case "forward-trace":
                        this.helperLine.addTailPoint(newOrigin)
                        break
                    case "backward-trace":
                        this.helperLine.addHeadPoint(newOrigin)
                        break
                    default:
                        assertNever(traceState)
                }
                doneTracing = false
            } else {
                doneTracing = true
            }
            if (doneTracing) {
                switch (traceState) {
                    case "forward-trace":
                        this.scheduleTraceStep("backward-trace")
                        break
                    case "backward-trace":
                        isComplete = true
                        break
                    default:
                        assertNever(traceState)
                }
            } else {
                // continue tracing
                this.scheduleTraceStep(traceState)
            }
        }

        this.imageOpNodeBag.endFrame()

        if (isComplete) {
            this.complete()
        }
    }

    private imageOpNodeBag = new ImageOpNodeBag("ImageOpNodeTilingHelperLineTracer")
    private unsubscribe$ = new Subject<void>()
    private performTraceStep$ = new Subject<TraceState>()
    private isAborted = false
}

type TraceState = "init" | "forward-trace" | "backward-trace"
